import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, COUNTRIES, fmtCurrency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Globe2, Building2, Briefcase, CreditCard, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PLANS = [
  { id: "plan_starter", name: "STARTER", price: 20000, users: 3, tag: "For small export teams" },
  { id: "plan_growth", name: "GROWTH", price: 35000, users: 10, tag: "Most popular", popular: true },
  { id: "plan_enterprise", name: "ENTERPRISE", price: 50000, users: "Custom", tag: "For large exporters" },
];

export default function Onboarding() {
  const { company, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({
    name: company?.name || "", website: "", country: company?.country || "", address: "",
    contact_number: "", email: company?.email || "", business_type: "both",
    primary_markets: "", product_categories: "", num_products: 0, monthly_enquiries: 0,
    monthly_rfqs: 0, monthly_orders: 0, avg_order_value: 0, gross_margin: 25, plan_id: "plan_growth",
  });
  const up = (k, v) => setF({ ...f, [k]: v });
  const [saving, setSaving] = useState(false);

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/company", {
        ...f,
        primary_markets: f.primary_markets ? f.primary_markets.split(",").map((s) => s.trim()) : [],
        product_categories: f.product_categories ? f.product_categories.split(",").map((s) => s.trim()) : [],
        onboarding_complete: true,
      });
      await refresh();
      toast.success("Your workspace is ready!");
      navigate("/dashboard");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const steps = [
    { n: 1, label: "Company", icon: Building2 },
    { n: 2, label: "Business", icon: Briefcase },
    { n: 3, label: "Plan", icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="h-16 bg-white border-b border-slate-200 flex items-center px-6">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-sky-500 flex items-center justify-center"><Globe2 className="h-5 w-5 text-white" /></div>
          <p className="font-bold font-display">PharmaExport Setup</p>
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center p-4 sm:p-8">
        <div className="w-full max-w-2xl">
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-8">
            {steps.map((s, i) => (
              <React.Fragment key={s.n}>
                <div className={`flex items-center gap-2 ${step >= s.n ? "text-slate-900" : "text-slate-400"}`}>
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold ${step > s.n ? "bg-emerald-500 text-white" : step === s.n ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>
                    {step > s.n ? <Check className="h-4 w-4" /> : s.n}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">{s.label}</span>
                </div>
                {i < steps.length - 1 && <div className={`w-8 sm:w-16 h-0.5 ${step > s.n ? "bg-emerald-500" : "bg-slate-200"}`} />}
              </React.Fragment>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
            {step === 1 && (
              <div className="space-y-4" data-testid="onboarding-step-1">
                <h2 className="text-xl font-bold font-display">Company Information</h2>
                <div className="space-y-1.5"><Label>Company name</Label><Input data-testid="onb-name" value={f.name} onChange={(e) => up("name", e.target.value)} className="bg-white" /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Website</Label><Input data-testid="onb-website" value={f.website} onChange={(e) => up("website", e.target.value)} className="bg-white" /></div>
                  <div className="space-y-1.5"><Label>Country</Label>
                    <Select value={f.country} onValueChange={(v) => up("country", v)}><SelectTrigger data-testid="onb-country" className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent className="bg-white max-h-60">{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Address</Label><Input data-testid="onb-address" value={f.address} onChange={(e) => up("address", e.target.value)} className="bg-white" /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Contact number</Label><Input data-testid="onb-contact" value={f.contact_number} onChange={(e) => up("contact_number", e.target.value)} className="bg-white" /></div>
                  <div className="space-y-1.5"><Label>Email</Label><Input data-testid="onb-email" value={f.email} onChange={(e) => up("email", e.target.value)} className="bg-white" /></div>
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-4" data-testid="onboarding-step-2">
                <h2 className="text-xl font-bold font-display">Business Information</h2>
                <div className="space-y-1.5"><Label>You are a</Label>
                  <Select value={f.business_type} onValueChange={(v) => up("business_type", v)}><SelectTrigger data-testid="onb-type" className="bg-white"><SelectValue /></SelectTrigger><SelectContent className="bg-white"><SelectItem value="manufacturer">Manufacturer</SelectItem><SelectItem value="exporter">Exporter</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label>Primary markets (comma separated)</Label><Input data-testid="onb-markets" value={f.primary_markets} onChange={(e) => up("primary_markets", e.target.value)} placeholder="Africa, Southeast Asia, Middle East" className="bg-white" /></div>
                <div className="space-y-1.5"><Label>Product categories (comma separated)</Label><Input data-testid="onb-categories" value={f.product_categories} onChange={(e) => up("product_categories", e.target.value)} placeholder="Antibiotics, Analgesics" className="bg-white" /></div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5"><Label>Monthly enquiries</Label><Input type="number" data-testid="onb-enquiries" value={f.monthly_enquiries} onChange={(e) => up("monthly_enquiries", Number(e.target.value))} className="bg-white" /></div>
                  <div className="space-y-1.5"><Label>Monthly RFQs</Label><Input type="number" data-testid="onb-rfqs" value={f.monthly_rfqs} onChange={(e) => up("monthly_rfqs", Number(e.target.value))} className="bg-white" /></div>
                  <div className="space-y-1.5"><Label>Monthly orders</Label><Input type="number" data-testid="onb-orders" value={f.monthly_orders} onChange={(e) => up("monthly_orders", Number(e.target.value))} className="bg-white" /></div>
                  <div className="space-y-1.5"><Label>Avg order value (₹)</Label><Input type="number" data-testid="onb-aov" value={f.avg_order_value} onChange={(e) => up("avg_order_value", Number(e.target.value))} className="bg-white" /></div>
                  <div className="space-y-1.5"><Label>Gross margin (%)</Label><Input type="number" data-testid="onb-margin" value={f.gross_margin} onChange={(e) => up("gross_margin", Number(e.target.value))} className="bg-white" /></div>
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-4" data-testid="onboarding-step-3">
                <h2 className="text-xl font-bold font-display">Choose your plan</h2>
                <div className="grid gap-3">
                  {PLANS.map((p) => (
                    <button key={p.id} onClick={() => up("plan_id", p.id)} data-testid={`onb-plan-${p.id}`}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${f.plan_id === p.id ? "border-sky-500 bg-sky-50/50" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold font-display">{p.name}</p>
                            {p.popular && <span className="text-[10px] bg-sky-500 text-white px-2 py-0.5 rounded-full font-semibold">POPULAR</span>}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{p.tag} · Up to {p.users} users</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold font-display">{fmtCurrency(p.price)}{p.id === "plan_enterprise" && "+"}</p>
                          <p className="text-xs text-slate-400">/month</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400">You can start using the platform immediately. Payment can be configured later.</p>
              </div>
            )}

            <div className="flex justify-between mt-8">
              <Button variant="outline" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="onb-back">Back</Button>
              {step < 3 ? (
                <Button onClick={() => setStep(step + 1)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="onb-next">Continue</Button>
              ) : (
                <Button onClick={finish} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="onb-finish">{saving ? "Setting up…" : "Complete setup"}</Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
